// Vendor
import { datadogLogs } from '@datadog/browser-logs';
import { differenceInDays, format, formatDistanceToNow } from 'date-fns';
import { FC, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { configVars } from 'src/helpers';

// Components
import Button, {
  ButtonDisabledStyle,
  ButtonSize,
  ButtonVariant
} from 'src/components/atoms/Button';
import {
  ButtonLinkColor,
  ButtonLinkSize,
  ButtonLinkVariant
} from 'src/components/atoms/ButtonLink';
import { Icon } from 'src/components/atoms/Icon';
import Table from 'src/components/atoms/Table';
import TableBody from 'src/components/atoms/TableBody';
import TableCell from 'src/components/atoms/TableCell';
import TableContainer from 'src/components/atoms/TableContainer';
import TableHead from 'src/components/atoms/TableHead';
import TableRow from 'src/components/atoms/TableRow';
import { TextColor, TextVariant } from 'src/components/atoms/Text';
import ButtonDownloadFiles from '../ButtonDownloadFiles';
import {
  Content,
  ActionsHeaderCell,
  ActionsMenu,
  ActionsMenuItem,
  ActionsOverlay,
  ActionsTableCell,
  Header,
  HeaderInfo,
  MonthlyGrossIncomeCell,
  ProofCount,
  StatusLabelCell,
  StyledButton,
  StyledIcon,
  StyledTag,
  StyledText
} from './styles';

// Helpers
import { useTransformResultToReadable } from 'src/features/fdeInbox/helpers';
import { cleanFileName } from 'src/helpers';
import { currencyFormat } from 'src/helpers/currencyFormat';

// Enums
import { EntryStatusEnum, Roles } from 'src/ts/enums';

// Types
import {
  EntryStatusType,
  IFDEInboxEntry,
  IFDEInboxEntryEntries,
  RoleType
} from 'src/ts/interfaces';

// Dispute
import DisputeModal from 'src/components/organisms/DisputeModal';

// ITL-2809: leasing-agent on-demand SF case creation
import { ConfirmationModal } from 'src/components/molecules/ConfirmationModal';
import proofsApi from 'src/features/proofs/api';
import useSnackbarProvider from 'src/hooks/useSnackbarProvider';

// Constants
import { columns, oldColumns } from './constants';

// Hooks
import { ToolTip } from 'src/components/atoms/ToolTip';
import { ToolTipDirection } from 'src/components/atoms/ToolTip/ToolTip';
import { LeasingTeam, SnapptTeam } from 'src/constants/roles';
import useUserRoles from 'src/features/auth/hooks/useUserRoles';
import { updateEntryThunk } from 'src/features/entries/services';
import { useFdeInfo, useGetFdeInbox } from 'src/features/fdeInbox';
import { useAppDispatch, RootState } from 'src/store';
import { useSelector, shallowEqual } from 'react-redux';
import { selectors as authSelectors } from 'src/features/auth/authSlice';
import { Feature, FeatureState } from 'src/ts/enums/feature';

export interface FolderRowProps {
  showReviewButton: boolean;
  folder: IFDEInboxEntry | null;
  role: RoleType;
}

// Accepted risk: PII (names, email) passed as URL query params to external marketing form.
// This is temporary — the form will be brought into the fraud platform frontend once proven effective.
const INCOME_CALCULATION_FORM_URL = 'https://snappt.com/lp/gsic-product-form/';

const buildIncomeCalculationFormUrl = (
  user: { first_name?: string; last_name?: string; email?: string } | null,
  row: IFDEInboxEntryEntries,
  propertyName?: string
) => {
  const params = new URLSearchParams();
  if (user?.first_name) params.set('firstname', user.first_name);
  if (user?.last_name) params.set('lastname', user.last_name);
  if (user?.email) params.set('email', user.email);
  if (row.applicant_first_name) params.set('gsic_applicant_first_name', row.applicant_first_name);
  if (row.applicant_last_name) params.set('gsic_applicant_last_name', row.applicant_last_name);
  if (row.applicant_id) params.set('gsic_applicant_id', row.applicant_id);
  if (propertyName) params.set('gsic_property_name', propertyName);
  const query = params.toString();
  return `${INCOME_CALCULATION_FORM_URL}${query ? `?${query}` : ''}`;
};

const parseSubmissionTimeDistance = (date: string) => {
  const submissionTime = new Date(date);
  // If date hasn't passed 1 day or more, use formatDistance utility
  // otherwise use simple format
  if (differenceInDays(submissionTime, new Date()) === 0) {
    const distance = formatDistanceToNow(submissionTime, {
      addSuffix: true
    });
    return `${distance.charAt(0).toUpperCase()}${distance.substring(1)}`;
  }

  return format(submissionTime, 'MM/dd/yyyy');
};

const BasicInfo = ({
  name,
  company,
  showReviewButton,
  folder_id
}: {
  name: string;
  company: string;
  showReviewButton: boolean;
  folder_id: string;
}) => {
  return (
    <Header>
      <HeaderInfo>
        <Icon icon="person-icon" color="gray900" />
        <StyledText variant={TextVariant.h4} color={TextColor.gray700}>
          {name}
        </StyledText>
      </HeaderInfo>
      <HeaderInfo>
        <Icon icon="company-model-icon" color="gray700" />
        <StyledText testId="property-name" variant={TextVariant.h4} color={TextColor.gray700}>
          {company}
        </StyledText>
      </HeaderInfo>
      <HeaderInfo>
        {showReviewButton && (
          <StyledButton
            to={`/folder/${folder_id}`}
            label="review_button"
            name="review_button"
            variant={ButtonLinkVariant.outline}
            size={ButtonLinkSize.medium}
            color={ButtonLinkColor.primary}
          >
            Review <StyledIcon icon="icon-arrow-right" color="primary" />
          </StyledButton>
        )}
      </HeaderInfo>
    </Header>
  );
};

const MonthlyGrossIncomeColumnHeader = ({
  incomeVerificationEnabledProperty,
  label,
  idx,
  width
}: {
  incomeVerificationEnabledProperty: boolean;
  label: string;
  idx: number;
  width: number;
}) => {
  const { isLeasingTeam } = useUserRoles();

  const showIncomeVerificationTooltip = !incomeVerificationEnabledProperty && isLeasingTeam;
  return (
    <TableCell key={idx} width={width} name="monthly-gross-income-column">
      {label}
      {showIncomeVerificationTooltip && (
        <ToolTip
          direction={ToolTipDirection.bottom_fit}
          content="Want your applicant's calculated income? Reach out to your Snappt contact for more information."
          className="monthly-gross-income-tooltip"
        >
          <Icon icon="help" />
        </ToolTip>
      )}
    </TableCell>
  );
};

const FolderRow: FC<FolderRowProps> = (props) => {
  const { showReviewButton, folder, role } = props;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { onGetFolders } = useGetFdeInbox();
  const { filters } = useFdeInfo();
  const [disputeEntryId, setDisputeEntryId] = useState<string | null>(null);
  // ITL-2809: state for the leasing-agent "Request Employment Verification"
  // confirmation modal. Mirrors the disputeEntryId pattern — set on click,
  // cleared on modal close.
  const [empVerifyEntryId, setEmpVerifyEntryId] = useState<string | null>(null);
  // ITL-2809 MAJ-1: track in-flight POST so we can disable the menu item
  // during submission and surface failures via the snackbar. A silent
  // failure here would re-render the same enabled button after the inbox
  // refresh, leaving the leasing agent unaware their request did not land.
  const [empVerifyIsSubmitting, setEmpVerifyIsSubmitting] = useState(false);
  const [openMenuRowId, setOpenMenuRowId] = useState<string | null>(null);
  const { showSnackbar, SnackTypes, VariantType } = useSnackbarProvider();

  // dispute_enabled is evaluated server-side per property via LaunchDarkly
  const disputeFromInboxEnabled = folder?.dispute_enabled ?? false;
  const incomeCalculationEnabled = folder?.income_calculation_enabled ?? false;
  const user = useSelector((state: RootState) => {
    const u = authSelectors.user(state);
    return u ? { first_name: u.first_name, last_name: u.last_name, email: u.email } : null;
  }, shallowEqual);

  const incomeVerificationEnabledProperty =
    folder?.property_features?.some(
      (propertyFeature) =>
        propertyFeature.feature_code === Feature.INCOME_VERIFICATION &&
        propertyFeature.state === FeatureState.ENABLED
    ) ?? false;

  const showNewColumns = incomeVerificationEnabledProperty;
  const transformResultToReadable = useTransformResultToReadable();

  const fdeColumns = showNewColumns ? columns : oldColumns;

  const isSnapptTeamUser = !!role && SnapptTeam.includes(role);
  // Marking an entry VIEWED is a leasing-team action. Checking !isSnapptTeamUser
  // instead would fire for any role that is in neither list and 401.
  const isLeasingTeam = !!role && LeasingTeam.includes(role);

  const checkIsEnabled = (status: EntryStatusType) => {
    if (isSnapptTeamUser) {
      return [EntryStatusEnum.Pending, EntryStatusEnum.Ready, EntryStatusEnum.Viewed].includes(
        status
      );
    }

    return [EntryStatusEnum.Ready, EntryStatusEnum.Viewed].includes(status);
  };

  const showDownload = () => {
    return role !== Roles.OnSiteRepresentative;
  };

  const hideViewColumn = () => {
    const allEntriesPending = folder?.entries?.every(
      (entry) => entry.status === EntryStatusEnum.Pending
    );
    return incomeVerificationEnabledProperty && allEntriesPending;
  };

  const shouldShowDisputeOption = () => {
    if (!disputeFromInboxEnabled) return false;

    // Keep role-based check as additional frontend guard
    return (
      role &&
      [
        Roles.Admin,
        Roles.BuildingManager,
        Roles.CompanyAdmin,
        Roles.OnSiteRepresentative,
        Roles.PropertyAdmin
      ].includes(role as Roles)
    );
  };

  const shouldShowIncomeCalculationOption = () => {
    if (!incomeCalculationEnabled) return false;

    return (
      role &&
      (
        [Roles.Admin, Roles.CompanyAdmin, Roles.BuildingManager, Roles.PropertyAdmin] as RoleType[]
      ).includes(role)
    );
  };

  // ITL-2809: leasing-agent cohort check shared by the row-level + column-
  // level eligibility for "Request Employment Verification". Eligibility
  // signals (OL exists / case not sent / non-OL not EDITED / reviews
  // complete) are resolved server-side and arrive on `row.employment_verification`;
  // this helper only gates on role + the entry's own field, not LD flags.
  const isLeasingAgentForEmploymentVerification = () =>
    role &&
    (
      [
        Roles.Admin,
        Roles.CompanyAdmin,
        Roles.BuildingManager,
        Roles.PropertyAdmin,
        Roles.OnSiteRepresentative
      ] as RoleType[]
    ).includes(role);

  const shouldShowEmploymentVerificationColumn = () => {
    if (!isLeasingAgentForEmploymentVerification()) return false;
    return folder?.entries?.some((entry) => entry.employment_verification != null) ?? false;
  };

  const shouldShowEmploymentVerificationOption = (row: IFDEInboxEntryEntries) =>
    isLeasingAgentForEmploymentVerification() && row.employment_verification != null;

  const shouldShowActionsColumn = () => {
    return (
      shouldShowDisputeOption() ||
      shouldShowIncomeCalculationOption() ||
      shouldShowEmploymentVerificationColumn()
    );
  };

  const onReportButtonClick = useCallback(
    async (row: IFDEInboxEntryEntries) => {
      if (
        isLeasingTeam &&
        checkIsEnabled(row.status as EntryStatusType) &&
        row.status === EntryStatusEnum.Ready &&
        row.id
      ) {
        await dispatch(updateEntryThunk({ id: row.id, status: EntryStatusEnum.Viewed }));
        onGetFolders(filters);
      }

      if (incomeVerificationEnabledProperty) {
        window.open(
          `${configVars.epa_url}/applicants/${row.applicant_id}/report?format=html&preset=summary`,
          '_blank'
        );
      } else {
        navigate(`/application/${row.id}/report`);
      }
    },
    [
      navigate,
      incomeVerificationEnabledProperty,
      checkIsEnabled,
      dispatch,
      onGetFolders,
      filters,
      isLeasingTeam
    ]
  );

  return (
    <Content>
      <BasicInfo
        folder_id={folder?.folder_id as string}
        name={folder?.folder_name as string}
        company={folder?.property_name as string}
        showReviewButton={showReviewButton}
      />

      <TableContainer className="fde-table">
        <Table>
          <TableHead>
            <TableRow>
              {fdeColumns.map(({ label, width, name }, idx) => {
                if (hideViewColumn() && name === 'report') return null;
                if (!showDownload() && name === 'doc_download') return null;
                if (
                  disputeFromInboxEnabled &&
                  (name === 'doc_number' || name === 'doc_download' || name === 'documents')
                )
                  return null;
                if (name === 'monthly_gross_income') {
                  return (
                    <MonthlyGrossIncomeColumnHeader
                      incomeVerificationEnabledProperty={incomeVerificationEnabledProperty}
                      key={idx}
                      label={label}
                      idx={idx}
                      width={width}
                    ></MonthlyGrossIncomeColumnHeader>
                  );
                }

                return (
                  <TableCell key={idx} width={width}>
                    {label}
                  </TableCell>
                );
              })}
              {shouldShowActionsColumn() && (
                <ActionsHeaderCell width={60}>Actions</ActionsHeaderCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {folder?.entries?.map((row: IFDEInboxEntryEntries) => {
              return (
                <TableRow key={row.id}>
                  <TableCell>{row.unit || 'Not Specified'}</TableCell>
                  <TableCell>
                    {parseSubmissionTimeDistance(row.submission_time as string)}
                  </TableCell>
                  <StatusLabelCell status={row.status as EntryStatusType}>
                    {transformResultToReadable(row.status)}
                  </StatusLabelCell>
                  <TableCell>
                    <StyledTag label={row.result} />
                  </TableCell>
                  {!hideViewColumn() && (
                    <TableCell>
                      <Button
                        isDisabled={!checkIsEnabled(row.status as EntryStatusType)}
                        onClick={() => onReportButtonClick(row)}
                        name="view_report_button"
                        variant={ButtonVariant.ghost}
                        disabledStyle={ButtonDisabledStyle.transparent}
                        size={ButtonSize.normal}
                      >
                        <Icon icon="icon-search" color="blue700" />
                      </Button>
                    </TableCell>
                  )}
                  {!showNewColumns && (
                    <>
                      {!disputeFromInboxEnabled && <TableCell>{row.proof_count}</TableCell>}
                      {showDownload() && !disputeFromInboxEnabled && (
                        <TableCell>
                          <ButtonDownloadFiles
                            applicantId={row.applicant_id}
                            isDisabled={!row.proof_count}
                            fileName={`${cleanFileName(row.applicant_full_name)}_Document`}
                          />
                        </TableCell>
                      )}
                    </>
                  )}

                  {showNewColumns && (
                    <>
                      {row.pending_specialty_review ? (
                        <TableCell>
                          <MonthlyGrossIncomeCell>Pending</MonthlyGrossIncomeCell>
                        </TableCell>
                      ) : row.income_calculation?.state === 'Available' ? (
                        <TableCell>{currencyFormat(row.income_calculation?.value || 0)}</TableCell>
                      ) : (
                        <TableCell>
                          <MonthlyGrossIncomeCell>
                            {row.income_calculation?.value
                              ? currencyFormat(row.income_calculation?.value || 0)
                              : row.income_calculation?.state || 'Not Available'}
                          </MonthlyGrossIncomeCell>
                        </TableCell>
                      )}
                      {!disputeFromInboxEnabled && (
                        <TableCell>
                          <ProofCount>{row.proof_count}</ProofCount>
                          {showDownload() && (
                            <ButtonDownloadFiles
                              className="align-download-button"
                              applicantId={row.applicant_id}
                              isDisabled={!row.proof_count}
                              fileName={`${cleanFileName(row.applicant_full_name)}_Document`}
                            />
                          )}
                        </TableCell>
                      )}
                    </>
                  )}

                  {shouldShowActionsColumn() && (
                    <ActionsTableCell width={60}>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!row.id) return;
                          setOpenMenuRowId(openMenuRowId === row.id ? null : row.id);
                        }}
                        name="open_actions_button"
                        variant={ButtonVariant.ghost}
                        disabledStyle={ButtonDisabledStyle.transparent}
                        size={ButtonSize.normal}
                        isDisabled={!row.id}
                      >
                        <Icon icon="more-vert" color="gray700" />
                      </Button>
                      {openMenuRowId === row.id && (
                        <>
                          <ActionsOverlay
                            role="button"
                            tabIndex={0}
                            onClick={() => setOpenMenuRowId(null)}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape' || e.key === 'Enter') {
                                setOpenMenuRowId(null);
                              }
                            }}
                          />
                          <ActionsMenu>
                            {shouldShowDisputeOption() && (
                              <>
                                {showDownload() && (
                                  <ActionsMenuItem
                                    disabled={!row.proof_count}
                                    onClick={() => {
                                      if (row.proof_count && row.applicant_id) {
                                        window.location.href = `${configVars.epa_url}/applicants/${row.applicant_id}/documents.zip`;
                                        setOpenMenuRowId(null);
                                      }
                                    }}
                                  >
                                    Download Documents ({row.proof_count || 0})
                                  </ActionsMenuItem>
                                )}
                                <ActionsMenuItem
                                  hasBorderTop={showDownload()}
                                  onClick={() => {
                                    row.id && setDisputeEntryId(row.id);
                                    setOpenMenuRowId(null);
                                  }}
                                >
                                  Log Dispute
                                </ActionsMenuItem>
                              </>
                            )}
                            {shouldShowIncomeCalculationOption() && (
                              <ActionsMenuItem
                                data-testid="request-income-calculation-menu-item"
                                hasBorderTop={shouldShowDisputeOption()}
                                onClick={() => {
                                  window.open(
                                    buildIncomeCalculationFormUrl(user, row, folder?.property_name),
                                    '_blank',
                                    'noopener,noreferrer'
                                  );
                                  setOpenMenuRowId(null);
                                }}
                              >
                                Request Income Calculation
                              </ActionsMenuItem>
                            )}
                            {/*
                              ITL-2809: leasing-agent on-demand SF case creation.
                              Renders directly below "Request Income Calculation"
                              when the eligibility object is present on the row
                              (server-side: property's offer_letter_verification
                              PropertyFeature is on). When enabled=false, renders
                              disabled with the server-supplied disabled_reason
                              as a hover tooltip; the ToolTip's `hidden` prop
                              suppresses the tooltip when the item is enabled.
                              `hasBorderTop` keys on the income-calc item's
                              presence so the divider is conditional.
                            */}
                            {shouldShowEmploymentVerificationOption(row) && (
                              <ToolTip
                                direction={ToolTipDirection.left}
                                content={row.employment_verification?.disabled_reason ?? ''}
                                hidden={row.employment_verification?.enabled !== false}
                              >
                                <ActionsMenuItem
                                  data-testid="request-employment-verification-menu-item"
                                  // ITL-2809 MAJ-2: divider renders when ANY item above
                                  // (dispute OR income-calc) is shown — not only when
                                  // income-calc is on. The prior predicate missed the
                                  // dispute-only case where the menu would render with
                                  // no visual separator between Log Dispute and this item.
                                  // `data-has-border-top` mirrors the prop for test
                                  // assertion stability (styled-components v5 filters
                                  // boolean props from the DOM, and `jest-styled-components`
                                  // is not in the toolchain).
                                  hasBorderTop={
                                    shouldShowDisputeOption() || shouldShowIncomeCalculationOption()
                                  }
                                  data-has-border-top={
                                    shouldShowDisputeOption() || shouldShowIncomeCalculationOption()
                                  }
                                  disabled={
                                    !row.employment_verification?.enabled || empVerifyIsSubmitting
                                  }
                                  onClick={() => {
                                    // The `disabled` prop already blocks pointer
                                    // interaction while a request is in flight or
                                    // the row is ineligible; the enabled guard here
                                    // just protects the state update.
                                    if (row.employment_verification?.enabled && row.id) {
                                      setEmpVerifyEntryId(row.id);
                                      setOpenMenuRowId(null);
                                    }
                                  }}
                                >
                                  Request Employment Verification
                                </ActionsMenuItem>
                              </ToolTip>
                            )}
                          </ActionsMenu>
                        </>
                      )}
                    </ActionsTableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {disputeEntryId && (
        <DisputeModal
          showModal={!!disputeEntryId}
          setShowModal={(show) => {
            if (!show) {
              setDisputeEntryId(null);
            }
          }}
          entryId={disputeEntryId}
          applicantName={
            folder?.entries?.find((entry) => entry.id === disputeEntryId)?.applicant_full_name
          }
        />
      )}
      {/*
        ITL-2809: confirmation modal for leasing-agent on-demand SF case
        creation. Body text names the applicant — PII appears in the
        modal UI ONLY, never in URLs or logs (contrast the legacy
        "Request Income Calculation" button which leaks PII via query
        params to an external form). On confirm, POST to the new
        endpoint and refresh the inbox so the eligibility flips to
        "Verification already requested" on success.
      */}
      {empVerifyEntryId && (
        <ConfirmationModal
          showConfirmationModal={!!empVerifyEntryId}
          setShowConfirmationModal={(show) => {
            if (!show) setEmpVerifyEntryId(null);
          }}
          modalTitle="Request Employment Verification"
          confirmButtonLabel="Send Request"
          modalText={`This will send ${
            folder?.entries?.find((entry) => entry.id === empVerifyEntryId)?.applicant_full_name ??
            'this applicant'
          }'s offer letter to the Snappt verification team for employer outreach.`}
          confirmationAction={async () => {
            // ITL-2809 MAJ-1: surface success/failure via the canonical
            // snackbar (mirrors useSendReport). The earlier console.error-
            // only path swallowed failures silently — browser console is
            // not monitored, and the inbox refresh re-rendered the same
            // enabled button so the leasing agent could not tell their
            // first click had failed. isSubmitting blocks the menu item
            // from re-firing during the in-flight POST.
            //
            // Capture the entry id into a stable local const: ConfirmationModal
            // calls setShowConfirmationModal(false) (which nulls
            // empVerifyEntryId, unmounting this modal) BEFORE awaiting this
            // handler, so reading the state var across the await is unreliable.
            // The captured const is used for the POST, the log, and both
            // outcome paths.
            const entryId = empVerifyEntryId;
            if (!entryId) return;

            setEmpVerifyIsSubmitting(true);
            try {
              await proofsApi.requestEmploymentVerification(entryId);
              await onGetFolders(filters);
              showSnackbar(
                VariantType.success,
                'Employment verification requested',
                SnackTypes.none
              );
            } catch (error) {
              // Structured Datadog Browser Logs signal (mirrors useSendReport)
              // so SF-case-creation failures are visible to monitoring rather
              // than swallowed in the unmonitored browser console. entry_id is
              // a UUID and safe to log.
              datadogLogs.logger.error('employment_verification_request_failed', {
                entry_id: entryId,
                error: error instanceof Error ? error.message : String(error)
              });
              showSnackbar(
                VariantType.error,
                'Failed to submit verification request. Please try again.',
                SnackTypes.none
              );
            } finally {
              setEmpVerifyIsSubmitting(false);
            }
          }}
        />
      )}
    </Content>
  );
};

export default FolderRow;
